/**
 * toSpeakable — render a markdown reply down to clean prose for browser TTS
 * (`speechSynthesis`). The frontend twin of felix-agent's
 * `capture_helpers.to_speakable` (#585).
 *
 * Background: Bob reported Felix narrating markdown aloud ("star star star")
 * and reciting UI-directive tokens in the meeting/Iterator voice path; that was
 * fixed in felix-agent. The main-app FelixChat "read aloud" path
 * (`useVoice.speak`) did its own lighter markdown strip that missed underscore
 * emphasis, code fences, numbered lists, control/directive tokens, and
 * whitespace collapse — so the same class of bug could surface here. This is
 * the single, tested place that strips formatting + machine tokens before
 * speech, keeping both voice surfaces consistent.
 *
 * Conservative: removes formatting syntax and machine-directive tokens, never
 * the speaker's actual words.
 */

// Control / machine-directive tokens (defensive — usually handled upstream).
const CONTROL_TOKEN = /\[(?:SILENT|CAPTURED[^\]]*|CURRENT SPEAKER[^\]]*)\]/gi;
// Function-call-shaped UI directives, e.g. `sidebar.open_category(plan)`.
const DIRECTIVE_CALL = /\b[a-z_]+(?:\.[a-z_]+)+\s*\([^)]*\)/g;
// Bracketed / braced directive blocks: [[...]], {{...}}.
const BRACKET_DIRECTIVE = /\[\[[^\]]*\]\]|\{\{[^}]*\}\}/g;
const CODE_FENCE = /```[\s\S]*?```/g;
const LINK = /\[([^\]]+)\]\([^)]*\)/g;
// Emphasis markers (**bold**, *italic*, __x__, _x_) + inline-code backticks.
const EMPHASIS = /(\*{1,3}|_{1,3}|`)/g;
// Leading block markers per line: #, >, bullets (- * +), numbered "1." / "2)".
const LINE_PREFIX = /^\s*(?:#{1,6}\s+|>\s+|[-*+]\s+|\d+[.)]\s+)/;

/**
 * Convert markdown to clean spoken text. Pure + deterministic.
 */
export function toSpeakable(text: string | null | undefined): string {
  if (!text) return "";
  let out = text.replace(CODE_FENCE, " ");
  out = out.replace(CONTROL_TOKEN, " ");
  out = out.replace(BRACKET_DIRECTIVE, " ");
  out = out.replace(DIRECTIVE_CALL, " ");
  out = out.replace(LINK, "$1"); // keep the link label, drop the URL
  // Strip per-line block prefixes before flattening to one spoken paragraph.
  out = out
    .split("\n")
    .map((line) => line.replace(LINE_PREFIX, ""))
    .join(" ");
  out = out.replace(EMPHASIS, "");
  out = out.replace(/\s+/g, " ").trim();
  return out;
}

export default toSpeakable;
